import {BackendNotInstalledError, injectCustomCode, loadBootstrapData} from '@common/supabase/bootstrap';
import {isLivechatWidgetPage} from '@common/supabase/supabase-client';
import {getCurrentTenant, UnknownTenantError} from '@common/supabase/tenant';
import {isPlatformHost} from './platform/platform-host';
import {setBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

// Laravel used to inline bootstrap data (settings, user, themes, translations)
// into the html page. With Supabase it's fetched before the app is rendered.
// The app itself is imported dynamically, so modules that read bootstrap data
// at import time see the loaded data.
async function boot() {
  // chat widget iframe (Laravel served it from a separate lc/widget page)
  if (isLivechatWidgetPage()) {
    const {bootLivechatWidget} = await import('@livechat/widget/widget-boot');
    await bootLivechatWidget();
    return;
  }

  await import('./app.css');

  // first-run setup wizard (Laravel's install/* routes): sets up the platform,
  // so it also works on the platform domain
  if (/^\/install(\/|$)/.test(window.location.pathname)) {
    const {renderInstallPage} = await import('./install/install-page');
    renderInstallPage();
    return;
  }

  // the platform's own site (landing page, signup, console), see platform-host.ts
  if (isPlatformHost()) {
    const {renderPlatformApp} = await import('./platform/platform-app');
    renderPlatformApp();
    return;
  }

  const data = await loadBootstrapData();
  // RedirectIfNotInstalledMiddleware: a fresh deploy goes to the setup wizard
  if (!(data.settings as any).site?.installed) {
    window.location.replace('/install');
    return;
  }
  // helpdesks suspended by the platform (or cancelled) are offline, platform
  // admins can still look inside
  const tenant = getCurrentTenant();
  if (
    tenant &&
    (tenant.status === 'suspended' || tenant.status === 'cancelled') &&
    !tenant.viewer_is_platform_admin
  ) {
    renderMessage('This helpdesk is not available', `${tenant.name} is currently offline.`);
    return;
  }
  setBootstrapData(data);
  applyFavicon((data.settings as any).branding?.favicon);
  injectCustomCode((data.settings as any).custom_code);
  const {renderApp} = await import('./render-app');
  renderApp();
}

// Laravel generated favicon sizes from the uploaded image (Settings > General)
function applyFavicon(url?: string) {
  if (!url) return;
  const href = /^(https?:)?\/\//.test(url) || url.startsWith('/') ? url : `/${url}`;
  document
    .querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')
    .forEach(el => el.setAttribute('href', href));
}

// plain page for when the app can't start (no translations or theme yet)
function renderMessage(title: string, text: string) {
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = '<div style="padding:24px;font-family:sans-serif"><h1></h1><p></p></div>';
    root.querySelector('h1')!.textContent = title;
    root.querySelector('p')!.textContent = text;
  }
}

boot().catch(err => {
  if (err instanceof BackendNotInstalledError) {
    window.location.replace('/install');
    return;
  }
  if (err instanceof UnknownTenantError) {
    renderMessage('There is no helpdesk at this address', window.location.host);
    return;
  }
  console.error(err);
  renderMessage('Could not connect to the backend', String(err?.message ?? err));
});
